<script setup>
import { computed, ref, watch } from 'vue';
import FluentIcon from 'dashboard/components/FluentIcon/Index.vue';
import {
  LINE_EMOJI_PACKS,
  buildLineEmojiImageUrl,
} from 'shared/helpers/lineEmoji';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'emoji-sent']);

const selectedProductId = ref(LINE_EMOJI_PACKS[0]?.productId || '');
watch(
  () => props.show,
  isVisible => {
    if (!isVisible) {
      return;
    }

    if (!selectedProductId.value && LINE_EMOJI_PACKS[0]) {
      selectedProductId.value = LINE_EMOJI_PACKS[0].productId;
    }
  }
);

const selectedPack = computed(() => {
  return (
    LINE_EMOJI_PACKS.find(pack => pack.productId === selectedProductId.value) ||
    LINE_EMOJI_PACKS[0]
  );
});

const emojiIds = computed(() => selectedPack.value?.emojiIds || []);

const selectPack = productId => {
  selectedProductId.value = productId;
};

const closeModal = () => {
  emit('close');
};

const selectEmoji = emojiId => {
  if (!selectedProductId.value || !emojiId) {
    return;
  }

  emit('emoji-sent', {
    productId: selectedProductId.value,
    emojiId,
  });
  closeModal();
};

const getEmojiUrl = emojiId => {
  return buildLineEmojiImageUrl({
    productId: selectedProductId.value,
    emojiId,
  });
};

const handleImageError = event => {
  event.target.style.opacity = '0.2';
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    @click="closeModal"
  >
    <div
      class="bg-white rounded-xl shadow-2xl w-[92%] max-w-2xl max-h-[85vh] flex flex-col"
      @click.stop
    >
      <div class="flex items-center justify-between p-4 border-b border-slate-200">
        <h3 class="text-lg font-semibold text-slate-900">Insert LINE Emoji</h3>
        <button
          class="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          @click="closeModal"
        >
          <FluentIcon icon="dismiss" size="20" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-4">
        <div class="flex gap-2 mb-4">
          <button
            v-for="pack in LINE_EMOJI_PACKS"
            :key="pack.productId"
            class="px-3 py-2 rounded-lg font-medium text-sm transition-all"
            :class="
              selectedProductId === pack.productId
                ? 'bg-woot-500 text-white shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            "
            @click="selectPack(pack.productId)"
          >
            {{ pack.name }}
          </button>
        </div>

        <div class="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-8 gap-2">
          <button
            v-for="emojiId in emojiIds"
            :key="emojiId"
            type="button"
            class="relative flex items-center justify-center p-2 rounded-lg border-2 transition-all"
            :class="'border-slate-200 bg-white hover:border-woot-300 hover:bg-woot-50'"
            @click="selectEmoji(emojiId)"
          >
            <img
              :src="getEmojiUrl(emojiId)"
              :alt="`LINE Emoji ${emojiId}`"
              class="w-7 h-7 object-contain"
              @error="handleImageError"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
